import { create } from 'zustand';
import { AxiosError } from 'axios';
import { HSE_TERMS, type HseTermKey } from '../constants/hseTerms';
import { getCollectionSites } from '../services/collection-site.service';
import { getHseWeeklyReport, saveHseWeeklyReport } from '../services/hse-weekly-report.service';
import { CollectionSiteSchema } from '../types/collection-site.type';
import type { HseWeeklyDraft, HseWeeklyReport, HseWeeklyRow } from '../types/hse-weekly-report.type';
import { defaultWeekStartIso } from '../utils/hseWeek.util';
import { draftFromReport, emptyDraft } from '../utils/hseWeeklyStorage.util';

const STORAGE_KEY = 'hseWeeklyPreferences';
const NOTES_MAX = 2000;

interface GenericResponse<T> {
  error?: { message?: string; code?: string } | null;
  response?: { message?: string; code?: string } | null;
  result: T;
}

function sortSites(sites: CollectionSiteSchema[]): CollectionSiteSchema[] {
  return [...sites].sort((a, b) => a.id.localeCompare(b.id));
}

function loadStoredPreferences(): { selectedSiteId: string | null; selectedWeekStart: string | null } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { selectedSiteId: null, selectedWeekStart: null };
    const parsed = JSON.parse(raw) as { selectedSiteId?: string; selectedWeekStart?: string };
    return {
      selectedSiteId: parsed.selectedSiteId ?? null,
      selectedWeekStart: parsed.selectedWeekStart ?? null,
    };
  } catch {
    return { selectedSiteId: null, selectedWeekStart: null };
  }
}

function parseWeeklyCount(s: string): { value?: number; invalid: boolean } {
  const t = s.trim();
  if (t === '') return { value: 0, invalid: false };
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return { invalid: true };
  return { value: n, invalid: false };
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as
      | { detail?: string; error?: { message?: string } }
      | undefined;
    return data?.detail || data?.error?.message || error.message || fallback;
  }
  return fallback;
}

interface HseWeeklyState {
  sites: CollectionSiteSchema[];
  selectedSiteId: string | null;
  selectedWeekStart: string;
  draft: HseWeeklyDraft;
  organizationId: string | null;
  isLoadingSites: boolean;
  isLoadingReport: boolean;
  isSaving: boolean;
  error: string | null;
  successMessage: string | null;
  clearError: () => void;
  clearSuccess: () => void;
  fetchSites: (organizationId: string) => Promise<void>;
  setSelectedSiteId: (siteId: string | null) => void;
  setSelectedWeekStart: (weekStart: string) => void;
  updateDraftRow: (termKey: HseTermKey, field: 'notes' | 'weeklyCount', value: string) => void;
  loadReport: () => Promise<void>;
  saveReport: () => Promise<boolean>;
  loadPreferences: () => void;
  savePreferences: () => void;
  resetForOrganizationChange: () => void;
}

export const useHseWeeklyStore = create<HseWeeklyState>((set, get) => ({
  sites: [],
  selectedSiteId: null,
  selectedWeekStart: defaultWeekStartIso(),
  draft: emptyDraft(),
  organizationId: null,
  isLoadingSites: false,
  isLoadingReport: false,
  isSaving: false,
  error: null,
  successMessage: null,

  clearError: () => set({ error: null }),
  clearSuccess: () => set({ successMessage: null }),

  loadPreferences: () => {
    const { selectedSiteId, selectedWeekStart } = loadStoredPreferences();
    set({
      ...(selectedWeekStart ? { selectedWeekStart } : {}),
      ...(selectedSiteId ? { selectedSiteId } : {}),
    });
  },

  savePreferences: () => {
    const { selectedSiteId, selectedWeekStart } = get();
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ selectedSiteId, selectedWeekStart }),
    );
  },

  resetForOrganizationChange: () => {
    set({
      sites: [],
      selectedSiteId: null,
      selectedWeekStart: defaultWeekStartIso(),
      draft: emptyDraft(),
      organizationId: null,
      isLoadingReport: false,
      error: null,
      successMessage: null,
    });
  },

  setSelectedSiteId: (siteId) => {
    set({ selectedSiteId: siteId });
    get().savePreferences();
    void get().loadReport();
  },

  setSelectedWeekStart: (weekStart) => {
    set({ selectedWeekStart: weekStart });
    get().savePreferences();
    void get().loadReport();
  },

  updateDraftRow: (termKey, field, value) => {
    set((state) => ({
      draft: {
        ...state.draft,
        [termKey]: {
          ...state.draft[termKey],
          [field]: value,
        },
      },
    }));
  },

  loadReport: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart } = get();
    if (!organizationId || !selectedSiteId) {
      set({ draft: emptyDraft(), isLoadingReport: false });
      return;
    }
    set({ isLoadingReport: true, error: null });
    try {
      const response = await getHseWeeklyReport(organizationId, selectedSiteId, selectedWeekStart);
      const data = response as GenericResponse<HseWeeklyReport>;
      set({
        draft: data.result ? draftFromReport(data.result) : emptyDraft(),
        isLoadingReport: false,
      });
    } catch (error) {
      set({
        draft: emptyDraft(),
        isLoadingReport: false,
        error: errorMessage(error, 'Failed to load Health & Safety report.'),
      });
    }
  },

  fetchSites: async (organizationId) => {
    set({ isLoadingSites: true, error: null, organizationId });
    try {
      const response = await getCollectionSites({ organization_id: organizationId });
      const data = response as GenericResponse<CollectionSiteSchema[]>;
      const sites = sortSites(data.result ?? []);
      const prefs = loadStoredPreferences();
      const preferredId = prefs.selectedSiteId;
      const validPreferred =
        preferredId && sites.some((s) => s.id === preferredId) ? preferredId : null;
      const nextSelected = validPreferred ?? (sites[0]?.id ?? null);
      const nextWeekStart = prefs.selectedWeekStart ?? defaultWeekStartIso();

      set({
        sites,
        selectedSiteId: nextSelected,
        selectedWeekStart: nextWeekStart,
        isLoadingSites: false,
      });
      get().savePreferences();
      await get().loadReport();
    } catch (error) {
      set({
        sites: [],
        selectedSiteId: null,
        isLoadingSites: false,
        error: errorMessage(error, 'Failed to load branches'),
      });
    }
  },

  saveReport: async () => {
    const { organizationId, selectedSiteId, selectedWeekStart, draft } = get();
    if (!organizationId || !selectedSiteId) {
      set({ error: 'Select a branch before saving.' });
      return false;
    }

    const rows: HseWeeklyRow[] = [];
    for (const term of HSE_TERMS) {
      const row = draft[term.key];
      const notes = row.notes.trim();
      if (notes.length > NOTES_MAX) {
        set({ error: `Notes for "${term.label}" exceed ${NOTES_MAX} characters.` });
        return false;
      }
      const parsed = parseWeeklyCount(row.weeklyCount);
      if (parsed.invalid) {
        set({ error: `Weekly count for "${term.label}" must be a non-negative whole number.` });
        return false;
      }
      rows.push({
        term_key: term.key,
        notes,
        weekly_count: parsed.value ?? 0,
      });
    }

    set({ isSaving: true, error: null, successMessage: null });
    try {
      const response = await saveHseWeeklyReport(organizationId, selectedSiteId, selectedWeekStart, { rows });
      const data = response as GenericResponse<HseWeeklyReport>;
      set({
        draft: data.result ? draftFromReport(data.result) : get().draft,
        isSaving: false,
        successMessage: 'Saved.',
      });
      return true;
    } catch (error) {
      set({
        isSaving: false,
        error: errorMessage(error, 'Failed to save Health & Safety report.'),
      });
      return false;
    }
  },
}));

export { NOTES_MAX };
